import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CommandStatus, DiagnosticsStep, DiagnosticsStepResult as ApiDiagnosticsStepResult } from '../api/api-reference';
import { SignalRService } from './signalr.service';

export interface DiagnosticsStepResult {
  step: DiagnosticsStep;
  meterNumber: string;
  status: CommandStatus;
  error?: { message?: string; errorCode?: string };
  executedTime?: string;
  endpoint?: string;
  lastCommunicationTime?: string;
  assetType?: string;
  assetName?: string;
  scannedCount?: number;
  failedCount?: number;
  expectedReadCount?: number;
  actualReadCount?: number;
}

/** Maps the steps returned by the HTTP endpoints onto the shape the hub sends. */
export function toDiagnosticsSteps(steps?: ApiDiagnosticsStepResult[]): DiagnosticsStepResult[] {
  return (steps ?? []).map((step) => ({
    step: step.step,
    meterNumber: step.meterNumber ?? '',
    status: step.status,
    error: step.error ? { message: step.error.message } : undefined,
    executedTime: step.executedTime?.toISOString(),
    endpoint: step.endpoint,
    lastCommunicationTime: step.lastCommunicationTime?.toISOString(),
    assetType: step.assetType,
    assetName: step.assetName,
    scannedCount: step.scannedCount,
    failedCount: step.failedCount,
    expectedReadCount: step.expectedReadCount,
    actualReadCount: step.actualReadCount,
  }));
}

export interface DiagnosticsClientResponse {
  commandId: string;
  meterNumber: string;
  status: CommandStatus;
  errorMessage?: string;
  diagnosticsTimeUtc?: string;
  steps?: DiagnosticsStepResult[];
}

/**
 * Owns the single 'diagnostics' hub handler and fans results out over rxjs.
 * SignalRService keeps only one handler per event name, so components must not
 * register 'diagnostics' themselves - they subscribe to results$ instead.
 */
@Injectable({ providedIn: 'root' })
export class DiagnosticsStateService {
  private readonly notificationPath = '/notification';
  private readonly eventName = 'diagnostics';

  private readonly results = new Subject<DiagnosticsClientResponse>();
  readonly results$: Observable<DiagnosticsClientResponse> = this.results.asObservable();

  constructor(private signalRService: SignalRService) {}

  // Safe to call from every consumer: re-registering our own callback replaces it with itself.
  async ensureSubscribed(): Promise<void> {
    try {
      await this.signalRService.startNewConnection(this.notificationPath);
      this.signalRService.registerHandler(
        this.notificationPath,
        this.eventName,
        (message: DiagnosticsClientResponse) => this.results.next(message),
      );
    } catch (error) {
      console.error('[DiagnosticsState] Failed to subscribe to diagnostics events:', error);
    }
  }
}
